<template>
  <div class="mapbox-pipeline-map">
    <!-- Map Controls -->
    <div class="map-controls">
      <v-btn-toggle v-model="mapMode" mandatory dense dark>
        <v-btn small value="overview">Overview</v-btn>
        <v-btn small value="detail">Detail</v-btn>
      </v-btn-toggle>
      <div class="zoom-controls">
        <v-btn icon small @click="zoomIn" :dark="isDarkMode">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-btn icon small @click="zoomOut" :dark="isDarkMode">
          <v-icon>mdi-minus</v-icon>
        </v-btn>
        <v-btn icon small @click="resetZoom" :dark="isDarkMode">
          <v-icon>mdi-fit-to-page</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Mapbox Container -->
    <div ref="mapContainer" class="map-container" :class="{ loading: isLoading }">
      <div v-if="isLoading" class="loading-overlay">
        <v-progress-circular indeterminate color="cyan"></v-progress-circular>
        <div class="loading-text">Loading Pipeline Map...</div>
      </div>
      <div v-if="error" class="error-overlay">
        <v-icon color="error" size="48">mdi-alert-circle</v-icon>
        <div class="error-text">{{ error }}</div>
        <v-btn small @click="initializeMap" color="cyan">Retry</v-btn>
      </div>
    </div>

    <!-- Map Tooltip -->
    <div 
      v-if="tooltip.show" 
      class="map-tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      <div class="tooltip-header">{{ tooltip.title }}</div>
      <div class="tooltip-content">{{ tooltip.content }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MapboxPipelineMap',
  props: {
    isDarkMode: {
      type: Boolean,
      default: true
    },
    pipelineData: {
      type: Array,
      default: () => []
    },
    selectedZone: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      map: null,
      mapboxgl: null,
      isLoading: true,
      error: null,
      mapMode: 'overview',
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        title: '',
        content: ''
      },
      pipelineSegments: []
    }
  },
  async mounted() {
    try {
      // Load Mapbox GL using the plugin
      this.mapboxgl = await this.$mapbox.loadMapbox();
      this.initializePipelineData();
      this.initializeMap();
    } catch (error) {
      console.error('Failed to load Mapbox:', error);
      this.error = 'Failed to load mapping library. Please check your internet connection and ensure Mapbox GL JS is loaded.';
      this.isLoading = false;
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
    }
  },
  watch: {
    selectedZone(newZone) {
      this.highlightZone(newZone)
    },
    mapMode(newMode) {
      this.updateMapView(newMode)
    }
  },
  methods: {
    initializePipelineData() {
      // Generate realistic pipeline segments with zones containing ~25 sensors per 100 miles
      this.pipelineSegments = [
        {
          id: 'segment-1',
          name: 'Amarillo to Liberal',
          healthStatus: 'healthy',
          coordinates: [
            [-101.8313, 35.2220], // Amarillo, TX
            [-101.2000, 35.8000],
            [-100.9000, 36.2000],
            [-100.5000, 36.6000],
            [-100.9203, 37.0431]  // Liberal, KS
          ],
          zones: [
            { 
              id: 'Z001', 
              name: 'Zone A1', 
              mileMarker: 15, 
              status: 'healthy', 
              alerts: 0,
          sensors: [
                { id: 'S002', name: 'Strain Gauge 1B', type: 'strain', value: '0.12 με', status: 'healthy' },
                { id: 'S003', name: 'Temperature Probe 1C', type: 'temperature', value: '68°F', status: 'healthy' },
                { id: 'S004', name: 'Movement Sensor 1D', type: 'movement', value: '0.001"', status: 'healthy' },
                { id: 'S006', name: 'Strain Gauge 1F', type: 'strain', value: '0.09 με', status: 'healthy' },
                { id: 'S007', name: 'Temperature Probe 1G', type: 'temperature', value: '69°F', status: 'healthy' },
                { id: 'S008', name: 'Movement Sensor 1H', type: 'movement', value: '0.002"', status: 'healthy' },
                { id: 'S010', name: 'Strain Gauge 1J', type: 'strain', value: '0.11 με', status: 'healthy' },
                { id: 'S011', name: 'Temperature Probe 1K', type: 'temperature', value: '67°F', status: 'healthy' },
                { id: 'S012', name: 'Movement Sensor 1L', type: 'movement', value: '0.001"', status: 'healthy' }
              ]
            },
            { 
              id: 'Z002', 
              name: 'Zone A2', 
              mileMarker: 35, 
              status: 'warning', 
              alerts: 1,
              sensors: [
                { id: 'S014', name: 'Strain Gauge 2B', type: 'strain', value: '0.18 με', status: 'warning' },
                { id: 'S015', name: 'Temperature Probe 2C', type: 'temperature', value: '72°F', status: 'healthy' },
                { id: 'S016', name: 'Movement Sensor 2D', type: 'movement', value: '0.003"', status: 'healthy' },
                { id: 'S018', name: 'Strain Gauge 2F', type: 'strain', value: '0.15 με', status: 'healthy' },
                { id: 'S019', name: 'Temperature Probe 2G', type: 'temperature', value: '71°F', status: 'healthy' },
                { id: 'S020', name: 'Movement Sensor 2H', type: 'movement', value: '0.002"', status: 'healthy' },
                { id: 'S022', name: 'Strain Gauge 2J', type: 'strain', value: '0.13 με', status: 'healthy' },
                { id: 'S023', name: 'Temperature Probe 2K', type: 'temperature', value: '70°F', status: 'healthy' },
                { id: 'S024', name: 'Movement Sensor 2L', type: 'movement', value: '0.001"', status: 'healthy' }
              ]
            }
          ]
        },
        {
          id: 'segment-2',
          name: 'Liberal to Dodge City',
          healthStatus: 'healthy',
          coordinates: [
            [-100.9203, 37.0431], // Liberal, KS
            [-100.5000, 37.4000],
            [-100.0000, 37.6000],
            [-99.9000, 37.7000],
            [-100.0167, 37.7528]  // Dodge City, KS
          ],
          zones: [
            { 
              id: 'Z003', 
              name: 'Zone B1', 
              mileMarker: 60, 
              status: 'healthy', 
              alerts: 0,
          sensors: [
                { id: 'S027', name: 'Strain Gauge 3B', type: 'strain', value: '0.10 με', status: 'healthy' },
                { id: 'S028', name: 'Temperature Probe 3C', type: 'temperature', value: '66°F', status: 'healthy' },
                { id: 'S029', name: 'Movement Sensor 3D', type: 'movement', value: '0.001"', status: 'healthy' },
                { id: 'S031', name: 'Strain Gauge 3F', type: 'strain', value: '0.08 με', status: 'healthy' },
              ]
            },
            { 
              id: 'Z004', 
              name: 'Zone B2', 
              mileMarker: 85, 
              status: 'healthy', 
              alerts: 0,
              sensors: [
                { id: 'S040', name: 'Strain Gauge 4B', type: 'strain', value: '0.14 με', status: 'healthy' },
                { id: 'S041', name: 'Temperature Probe 4C', type: 'temperature', value: '69°F', status: 'healthy' },
                { id: 'S042', name: 'Movement Sensor 4D', type: 'movement', value: '0.001"', status: 'healthy' },
                { id: 'S044', name: 'Strain Gauge 4F', type: 'strain', value: '0.11 με', status: 'healthy' },
                { id: 'S045', name: 'Temperature Probe 4G', type: 'temperature', value: '68°F', status: 'healthy' },
                { id: 'S046', name: 'Movement Sensor 4H', type: 'movement', value: '0.002"', status: 'healthy' },
                { id: 'S048', name: 'Strain Gauge 4J', type: 'strain', value: '0.12 με', status: 'healthy' },
                { id: 'S049', name: 'Temperature Probe 4K', type: 'temperature', value: '70°F', status: 'healthy' },
                { id: 'S050', name: 'Movement Sensor 4L', type: 'movement', value: '0.001"', status: 'healthy' }
              ]
            }
          ]
        },
        {
          id: 'segment-3',
          name: 'Dodge City to Garden City',
          healthStatus: 'warning',
          coordinates: [
            [-100.0167, 37.7528], // Dodge City, KS
            [-100.5000, 37.8000],
            [-100.8000, 37.9000],
            [-100.8731, 37.9717]  // Garden City, KS
          ],
          zones: [
            { 
              id: 'Z005', 
              name: 'Zone C1', 
              mileMarker: 110, 
              status: 'warning', 
              alerts: 2,
          sensors: [
                { id: 'S052', name: 'Strain Gauge 5B', type: 'strain', value: '0.22 με', status: 'warning' },
                { id: 'S053', name: 'Temperature Probe 5C', type: 'temperature', value: '75°F', status: 'healthy' },
                { id: 'S054', name: 'Movement Sensor 5D', type: 'movement', value: '0.004"', status: 'healthy' },
                { id: 'S056', name: 'Strain Gauge 5F', type: 'strain', value: '0.16 με', status: 'healthy' },
                { id: 'S057', name: 'Temperature Probe 5G', type: 'temperature', value: '74°F', status: 'healthy' },
                { id: 'S058', name: 'Movement Sensor 5H', type: 'movement', value: '0.003"', status: 'healthy' },
                { id: 'S060', name: 'Strain Gauge 5J', type: 'strain', value: '0.18 με', status: 'healthy' },
                { id: 'S061', name: 'Temperature Probe 5K', type: 'temperature', value: '73°F', status: 'healthy' },
                { id: 'S062', name: 'Movement Sensor 5L', type: 'movement', value: '0.002"', status: 'healthy' }
              ]
            },
            { 
              id: 'Z006', 
              name: 'Zone C2', 
              mileMarker: 135, 
              status: 'critical', 
              alerts: 3,
              sensors: [
                { id: 'S065', name: 'Strain Gauge 6B', type: 'strain', value: '0.28 με', status: 'critical' },
                { id: 'S066', name: 'Temperature Probe 6C', type: 'temperature', value: '78°F', status: 'warning' },
                { id: 'S067', name: 'Movement Sensor 6D', type: 'movement', value: '0.006"', status: 'warning' },
                { id: 'S069', name: 'Strain Gauge 6F', type: 'strain', value: '0.25 με', status: 'warning' },
                { id: 'S070', name: 'Temperature Probe 6G', type: 'temperature', value: '77°F', status: 'healthy' },
                { id: 'S071', name: 'Movement Sensor 6H', type: 'movement', value: '0.005"', status: 'warning' },
                { id: 'S073', name: 'Strain Gauge 6J', type: 'strain', value: '0.23 με', status: 'healthy' },
                { id: 'S074', name: 'Temperature Probe 6K', type: 'temperature', value: '76°F', status: 'healthy' },
                { id: 'S075', name: 'Movement Sensor 6L', type: 'movement', value: '0.004"', status: 'healthy' }
              ]
            }
          ]
        },
        {
          id: 'segment-4',
          name: 'Garden City to Hays',
          healthStatus: 'warning',
          coordinates: [
            [-100.8731, 37.9717], // Garden City, KS
            [-100.5000, 38.2000],
            [-100.0000, 38.4000],
            [-99.3000, 38.6000],
            [-99.3269, 38.8731]   // Hays, KS (ENDPOINT)
          ],
          zones: [
            { 
              id: 'Z007', 
              name: 'Zone D1', 
              mileMarker: 160, 
              status: 'warning', 
              alerts: 1,
          sensors: [
                { id: 'S078', name: 'Strain Gauge 7B', type: 'strain', value: '0.19 με', status: 'warning' },
                { id: 'S079', name: 'Temperature Probe 7C', type: 'temperature', value: '71°F', status: 'healthy' },
                { id: 'S080', name: 'Movement Sensor 7D', type: 'movement', value: '0.003"', status: 'healthy' },
                { id: 'S082', name: 'Strain Gauge 7F', type: 'strain', value: '0.14 με', status: 'healthy' },
                { id: 'S083', name: 'Temperature Probe 7G', type: 'temperature', value: '70°F', status: 'healthy' },
                { id: 'S084', name: 'Movement Sensor 7H', type: 'movement', value: '0.002"', status: 'healthy' },
                { id: 'S086', name: 'Strain Gauge 7J', type: 'strain', value: '0.13 με', status: 'healthy' },
                { id: 'S087', name: 'Temperature Probe 7K', type: 'temperature', value: '72°F', status: 'healthy' },
                { id: 'S088', name: 'Movement Sensor 7L', type: 'movement', value: '0.001"', status: 'healthy' }
              ]
            },
            { 
              id: 'Z008', 
              name: 'Zone D2', 
              mileMarker: 185, 
              status: 'healthy', 
              alerts: 0,
          sensors: [
                { id: 'S091', name: 'Strain Gauge 8B', type: 'strain', value: '0.11 με', status: 'healthy' },
                { id: 'S092', name: 'Temperature Probe 8C', type: 'temperature', value: '68°F', status: 'healthy' },
                { id: 'S093', name: 'Movement Sensor 8D', type: 'movement', value: '0.001"', status: 'healthy' },
                { id: 'S095', name: 'Strain Gauge 8F', type: 'strain', value: '0.09 με', status: 'healthy' },
                { id: 'S096', name: 'Temperature Probe 8G', type: 'temperature', value: '67°F', status: 'healthy' },
                { id: 'S097', name: 'Movement Sensor 8H', type: 'movement', value: '0.002"', status: 'healthy' },
                { id: 'S099', name: 'Strain Gauge 8J', type: 'strain', value: '0.10 με', status: 'healthy' },
                { id: 'S100', name: 'Temperature Probe 8K', type: 'temperature', value: '69°F', status: 'healthy' },
                { id: 'S101', name: 'Movement Sensor 8L', type: 'movement', value: '0.001"', status: 'healthy' }
              ]
            }
          ]
        }
      ]
    },

    async initializeMap() {
      try {
        this.isLoading = true
        this.error = null

        // Check if mapboxgl was loaded
        if (!this.mapboxgl) {
          this.error = 'Mapbox library not loaded. Please refresh the page.';
          this.isLoading = false;
          return;
        }

        // Set your Mapbox access token
        const mapboxToken = process.env.VUE_APP_MAPBOX_TOKEN
        
        // Enhanced token validation
        console.log('🗺️ Mapbox Debug Info:');
        console.log('Token exists:', !!mapboxToken);
        console.log('Token length:', mapboxToken ? mapboxToken.length : 0);
        console.log('Token starts with pk.:', mapboxToken ? mapboxToken.startsWith('pk.') : false);
        console.log('Environment:', process.env.NODE_ENV);
        
        if (!mapboxToken) {
          this.error = 'Mapbox access token not found. Please add VUE_APP_MAPBOX_TOKEN to your .env file.';
          this.isLoading = false;
          return;
        }
        
        if (mapboxToken.includes('your') || mapboxToken.includes('example') || mapboxToken.length < 50) {
          this.error = 'Please replace the placeholder Mapbox token with your actual token from https://account.mapbox.com/access-tokens/';
          this.isLoading = false;
          return;
        }

        if (!mapboxToken.startsWith('pk.')) {
          this.error = 'Invalid Mapbox token format. Token should start with "pk."';
          this.isLoading = false;
          return;
        }

        this.mapboxgl.accessToken = mapboxToken

        console.log('🗺️ Initializing map with token:', mapboxToken.substring(0, 20) + '...');

        // Initialize map
        this.map = new this.mapboxgl.Map({
          container: this.$refs.mapContainer,
          style: this.isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
          center: [-99.5, 37.5], // Center on Kansas/Oklahoma border
          zoom: 6,
          bearing: 0,
          pitch: 0
        })

        // Add map controls
        this.map.addControl(new this.mapboxgl.NavigationControl(), 'top-right')

        // Enhanced event handlers for debugging
        this.map.on('load', () => {
          console.log('✅ Map loaded successfully');
          this.addPipelineData()
          this.addCityMarkers()
          this.setupEventHandlers()
          
          // Validate feature structure after everything is loaded
          setTimeout(() => {
            this.validateFeatureStructure()
          }, 1000)
          
          this.isLoading = false
        })

        this.map.on('error', (e) => {
          console.error('🚨 Mapbox error details:', e);
          console.error('Error type:', e.error?.type);
          console.error('Error message:', e.error?.message);
          console.error('Error status:', e.error?.status);
          
          // Specific error handling
          if (e.error && e.error.message) {
            const errorMsg = e.error.message.toLowerCase();
            const status = e.error.status;
            
            if (status === 401 || errorMsg.includes('unauthorized') || errorMsg.includes('401')) {
              this.error = 'Invalid Mapbox access token. Please verify your token at https://account.mapbox.com/access-tokens/';
            } else if (status === 403 || errorMsg.includes('forbidden')) {
              this.error = 'Mapbox token permissions insufficient. Please check token scopes in your Mapbox account.';
            } else if (status === 429 || errorMsg.includes('rate limit')) {
              this.error = 'Mapbox API rate limit exceeded. Please wait a few minutes and try again.';
            } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
              this.error = 'Network error loading map tiles. Please check your internet connection.';
          } else {
              this.error = `Mapbox error: ${e.error.message}`;
            }
          } else {
            this.error = 'Failed to load map. Please check your Mapbox token and internet connection.';
          }
          this.isLoading = false
        })

        // Add network monitoring
        this.map.on('dataloading', (e) => {
          if (e.sourceDataType === 'visibility') {
            console.log('📡 Loading map data...');
          }
        });

        this.map.on('data', (e) => {
          if (e.sourceDataType === 'visibility') {
            console.log('✅ Map data loaded');
          }
        });

        this.map.on('sourcedata', (e) => {
          if (e.isSourceLoaded) {
            console.log('✅ Source data loaded:', e.sourceId);
          }
        });

      } catch (error) {
        console.error('🚨 Map initialization error:', error)
        if (error.message && error.message.includes('token')) {
          this.error = 'Mapbox token error. Please check your VUE_APP_MAPBOX_TOKEN configuration.'
        } else {
          this.error = 'Failed to initialize map. Please refresh the page.'
        }
        this.isLoading = false
      }
    },

    addPipelineData() {
      // Add pipeline segments as lines
      this.pipelineSegments.forEach((segment, index) => {
        const sourceId = `pipeline-${segment.id}`
        const layerId = `pipeline-layer-${segment.id}`

        // Add source with properly structured GeoJSON including feature ID
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [{
              id: segment.id, // Add explicit feature ID
            type: 'Feature',
            properties: {
              segmentId: segment.id,
              name: segment.name,
              healthStatus: segment.healthStatus,
                zoneCount: segment.zones.length,
                totalAlerts: segment.zones.reduce((acc, zone) => acc + zone.alerts, 0),
                totalSensors: segment.zones.reduce((acc, zone) => acc + zone.sensors.length, 0)
            },
            geometry: {
              type: 'LineString',
              coordinates: segment.coordinates
            }
            }]
          }
        })

        // Add line layer
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#00d4ff', // Bright cyan for selected
              this.getSegmentColor(segment.healthStatus)
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              10, // Wider when selected
              [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
                8, // Medium when hovered
                6  // Normal width
              ]
            ],
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              1.0, // Full opacity when selected
              0.8  // Normal opacity
            ]
          }
        })

        // Add zone markers for this segment (instead of individual sensors)
        segment.zones.forEach(zone => {
          const zoneCoord = this.interpolateCoordinate(segment.coordinates, zone.mileMarker, 200) // Total pipeline ~200 miles
          const zoneSourceId = `zone-${zone.id}`
          const zoneLayerId = `zone-layer-${zone.id}`
          
          this.map.addSource(zoneSourceId, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [{
                id: zone.id, // Add explicit feature ID for zone
              type: 'Feature',
              properties: {
                  zoneId: zone.id,
                  zoneName: zone.name,
                  mileMarker: zone.mileMarker,
                  status: zone.status,
                  alerts: zone.alerts,
                  segmentId: segment.id,
                  sensorCount: zone.sensors.length,
                  type: 'zone' // Add type for identification
              },
              geometry: {
                type: 'Point',
                  coordinates: zoneCoord
              }
              }]
            }
          })

          // Create zone marker layer with better styling
          this.map.addLayer({
            id: zoneLayerId,
            type: 'circle',
            source: zoneSourceId,
            paint: {
              'circle-radius': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                12, // Larger when hovered
                8   // Normal size (larger than individual sensors)
              ],
              'circle-color': this.getZoneColor(zone.status),
              'circle-stroke-width': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                4, // Thicker stroke when hovered
                2  // Normal stroke
              ],
              'circle-stroke-color': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                '#00d4ff', // Cyan stroke when hovered
                '#ffffff'  // White stroke normally
              ],
              'circle-opacity': 0.9
            }
          })

          // Add zone label layer
          this.map.addLayer({
            id: `zone-label-${zone.id}`,
            type: 'symbol',
            source: zoneSourceId,
            layout: {
              'text-field': zone.name,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-offset': [0, 2],
              'text-anchor': 'top',
              'text-size': 11
            },
            paint: {
              'text-color': this.isDarkMode ? '#ffffff' : '#333333',
              'text-halo-color': this.isDarkMode ? '#000000' : '#ffffff',
              'text-halo-width': 1
            }
          })
        })
      })
    },

    addCityMarkers() {
      const cities = [
        { name: 'Amarillo', coordinates: [-101.8313, 35.2220] },
        { name: 'Liberal', coordinates: [-100.9203, 37.0431] },
        { name: 'Dodge City', coordinates: [-100.0167, 37.7528] },
        { name: 'Garden City', coordinates: [-100.8731, 37.9717] },
        { name: 'Hays', coordinates: [-99.3269, 38.8731] }
      ]

      cities.forEach(city => {
        // Add city label
        this.map.addSource(`city-${city.name}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              name: city.name
            },
            geometry: {
              type: 'Point',
              coordinates: city.coordinates
            }
          }
        })

        this.map.addLayer({
          id: `city-label-${city.name}`,
          type: 'symbol',
          source: `city-${city.name}`,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 1.25],
            'text-anchor': 'top',
            'text-size': 12
          },
          paint: {
            'text-color': this.isDarkMode ? '#ffffff' : '#333333',
            'text-halo-color': this.isDarkMode ? '#000000' : '#ffffff',
            'text-halo-width': 1
          }
        })
      })
    },

    setupEventHandlers() {
      // Pipeline segment hover effects
      this.pipelineSegments.forEach(segment => {
        const layerId = `pipeline-layer-${segment.id}`
        const sourceId = `pipeline-${segment.id}`
        
        this.map.on('mouseenter', layerId, (e) => {
          this.map.getCanvas().style.cursor = 'pointer'
          
          // Check if feature has ID before setting state
          const feature = e.features[0]
          if (feature && feature.id !== undefined) {
            try {
          this.map.setFeatureState(
                { source: sourceId, id: feature.id },
            { hover: true }
          )
            } catch (error) {
              console.warn('Failed to set feature state for pipeline segment:', error)
            }
          }
          
          this.showTooltip(e, {
            title: segment.name,
            content: `${segment.healthStatus.toUpperCase()} • ${segment.zones.length} zones • ${segment.zones.reduce((acc, z) => acc + z.alerts, 0)} alerts`
          })
        })

        this.map.on('mouseleave', layerId, (e) => {
          this.map.getCanvas().style.cursor = ''
          
          // Check if feature has ID before setting state
          const feature = e.features[0]
          if (feature && feature.id !== undefined) {
            try {
          this.map.setFeatureState(
                { source: sourceId, id: feature.id },
            { hover: false }
          )
            } catch (error) {
              console.warn('Failed to unset feature state for pipeline segment:', error)
            }
          }
          
          this.hideTooltip()
        })

        this.map.on('click', layerId, (e) => {
          const feature = e.features[0]
          console.log('Pipeline segment clicked:', {
            segmentId: segment.id,
            featureId: feature.id,
            properties: feature.properties
          })
          
          this.$emit('segment-selected', {
            segment,
            feature,
            coordinates: e.lngLat
          })
        })

        // Zone hover effects
        segment.zones.forEach(zone => {
          const zoneLayerId = `zone-layer-${zone.id}`
          const zoneSourceId = `zone-${zone.id}`
          
          this.map.on('mouseenter', zoneLayerId, (e) => {
            this.map.getCanvas().style.cursor = 'pointer'
            
            // Check if feature has ID before setting state
            const feature = e.features[0]
            if (feature && feature.id !== undefined) {
              try {
            this.map.setFeatureState(
                  { source: zoneSourceId, id: feature.id },
              { hover: true }
            )
              } catch (error) {
                console.warn('Failed to set feature state for zone:', error)
              }
            }
            
            // Emit zone hover event with coordinates for popup positioning
            const rect = this.$refs.mapContainer.getBoundingClientRect()
            this.$emit('zone-hover', {
              zone,
              segment,
              feature,
              x: e.point.x + rect.left,
              y: e.point.y + rect.top,
              coordinates: e.lngLat
            })
            
            // Don't show tooltip when popup will show
            // this.showTooltip(e, {
            //   title: zone.name,
            //   content: `Mile ${zone.mileMarker} • ${zone.status.toUpperCase()} • ${zone.sensors.length} fastener sensors • ${zone.alerts} alerts`
            // })
          })

          this.map.on('mouseleave', zoneLayerId, (e) => {
            this.map.getCanvas().style.cursor = ''
            
            // Check if feature has ID before setting state
            const feature = e.features[0]
            if (feature && feature.id !== undefined) {
              try {
            this.map.setFeatureState(
                  { source: zoneSourceId, id: feature.id },
              { hover: false }
            )
              } catch (error) {
                console.warn('Failed to unset feature state for zone:', error)
              }
            }
            
            // Emit zone hover end event
            this.$emit('zone-hover-end')
            
            this.hideTooltip()
          })

          this.map.on('click', zoneLayerId, (e) => {
            const feature = e.features[0]
            console.log('🎯 Zone clicked:', {
              zoneId: zone.id,
              zoneName: zone.name,
              featureId: feature.id,
              properties: feature.properties,
              sensorCount: zone.sensors.length
            })
            
            // Emit zone-selected event
            this.$emit('zone-selected', {
              zone,
              segment,
              feature,
              coordinates: e.lngLat
            })
            
            console.log('✅ Zone-selected event emitted for:', zone.name)
          })
        })
      })
    },

    getSegmentColor(healthStatus) {
      const colors = {
        healthy: '#00ff88',
        warning: '#ffaa00',
        critical: '#ff6600'
      }
      return colors[healthStatus] || '#666666'
    },

    getZoneColor(status) {
      const colors = {
        healthy: '#00ff88',
        warning: '#ffdd00',
        critical: '#ff6600'
      }
      return colors[status] || '#666666'
    },

    interpolateCoordinate(coordinates, mileMarker, totalMiles) {
      // Simple interpolation to place zones along the pipeline
      const progress = mileMarker / totalMiles
      const segmentIndex = Math.floor(progress * (coordinates.length - 1))
      const segmentProgress = (progress * (coordinates.length - 1)) - segmentIndex
      
      if (segmentIndex >= coordinates.length - 1) {
        return coordinates[coordinates.length - 1]
      }
      
      const start = coordinates[segmentIndex]
      const end = coordinates[segmentIndex + 1]
      
      return [
        start[0] + (end[0] - start[0]) * segmentProgress,
        start[1] + (end[1] - start[1]) * segmentProgress
      ]
    },

    showTooltip(event, data) {
      const rect = this.$refs.mapContainer.getBoundingClientRect()
      this.tooltip = {
        show: true,
        x: event.point.x + 10,
        y: event.point.y - 10,
        title: data.title,
        content: data.content
      }
    },

    hideTooltip() {
      this.tooltip.show = false
    },

    zoomIn() {
      this.map.zoomIn()
    },

    zoomOut() {
      this.map.zoomOut()
    },

    resetZoom() {
      this.map.flyTo({
        center: [-99.5, 37.5],
        zoom: 6,
        bearing: 0,
        pitch: 0
      })
    },

    updateMapView(mode) {
      if (mode === 'detail') {
        this.map.flyTo({ zoom: 8 })
      } else {
        this.map.flyTo({ zoom: 6 })
      }
    },

    highlightZone(zone) {
      // Highlight selected zone/segment
      if (zone) {
        console.log('Highlighting zone:', zone)
        
        const segment = this.pipelineSegments.find(s => 
          s.zones.some(z => z.id === zone.zoneId)
        )
        
        if (segment) {
          console.log('Found segment for zone:', segment.id)
          
          // Fit bounds to the segment
          const bounds = new this.mapboxgl.LngLatBounds()
          segment.coordinates.forEach(coord => bounds.extend(coord))
          this.map.fitBounds(bounds, { padding: 50 })
          
          // Highlight the segment feature
          const sourceId = `pipeline-${segment.id}`
          try {
            // First, reset all other segments
            this.pipelineSegments.forEach(seg => {
              if (seg.id !== segment.id) {
                this.map.setFeatureState(
                  { source: `pipeline-${seg.id}`, id: seg.id },
                  { selected: false }
                )
              }
            })
            
            // Then highlight the selected segment
            this.map.setFeatureState(
              { source: sourceId, id: segment.id },
              { selected: true }
            )
            
            console.log('Successfully highlighted segment:', segment.id)
          } catch (error) {
            console.warn('Failed to highlight segment:', error)
          }
        } else {
          console.warn('Segment not found for zone:', zone.zoneId)
        }
      } else {
        // Clear all highlights
        this.pipelineSegments.forEach(segment => {
          try {
            this.map.setFeatureState(
              { source: `pipeline-${segment.id}`, id: segment.id },
              { selected: false }
            )
          } catch (error) {
            console.warn('Failed to clear highlight for segment:', segment.id, error)
          }
        })
      }
    },

    // Add helper method to validate feature structure
    validateFeatureStructure() {
      console.log('🔍 Validating pipeline feature structure...')
      
      this.pipelineSegments.forEach(segment => {
        const sourceId = `pipeline-${segment.id}`
        
        // Query the source to check if features have IDs
        try {
          const features = this.map.querySourceFeatures(sourceId)
          console.log(`Segment ${segment.id}:`, {
            sourceId,
            featureCount: features.length,
            hasFeatureIds: features.every(f => f.id !== undefined),
            featureIds: features.map(f => f.id),
            sampleFeature: features[0]
          })
        } catch (error) {
          console.warn(`Failed to query features for segment ${segment.id}:`, error)
        }
      })
    }
  }
}
</script>

<style scoped>
.mapbox-pipeline-map {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--card-bg, #2d2d2d);
  border-radius: 8px;
}

.map-controls {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: 8px;
}

.zoom-controls {
  display: flex;
  gap: 4px;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  z-index: 1000;
  border-radius: 8px;
}

.loading-text,
.error-text {
  margin-top: 16px;
  font-size: 14px;
  text-align: center;
}

.error-text {
  margin-bottom: 16px;
}

.map-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  border: 1px solid #333;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 250px;
}

.tooltip-header {
  font-weight: 600;
  margin-bottom: 4px;
  color: #00d4ff;
}

.tooltip-content {
  font-size: 11px;
  opacity: 0.9;
  line-height: 1.3;
}

/* Mapbox GL overrides for dark theme */
::v-deep .mapboxgl-ctrl-group {
  background: rgba(0, 0, 0, 0.8) !important;
  backdrop-filter: blur(8px);
}

::v-deep .mapboxgl-ctrl button {
  background-color: transparent !important;
  color: white !important;
}

::v-deep .mapboxgl-ctrl button:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Hide default Mapbox attribution */
::v-deep .mapboxgl-ctrl-attrib {
  display: none !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .map-controls {
    flex-direction: column;
    gap: 8px;
    padding: 6px;
  }
  
  .zoom-controls {
    flex-direction: column;
    gap: 2px;
  }
}
</style> 